<script setup lang="ts">
const props = defineProps<{
  product: SingleProduct
}>()

const cards = computed(() => {
  if (!props.product.heroCards) {
    return []
  }

  return props.product.heroCards.content.slice(0, 6).map((content, index) => ({
    ...content,
    img: props.product.heroCards?.mascotImages?.[index],
  }))
})
</script>

<template>
  <UContainer
    v-if="cards.length > 0"
    as="ul"
    class="cards"
  >
    <li
      v-for="card in cards"
      :key="card.text"
      class="relative"
      :style="{
        backgroundColor: card?.backgroundColor,
        color: card?.textColor,
      }"
    >
      <CrystallizeImage
        :image="card.img"
        class="inset-0 m-auto max-w-full sm:absolute sm:max-h-[70%] sm:max-w-[50%]"
        img-class="sm:max-h-full sm:mx-auto"
        data-mascot
        sizes="200px"
      />
      {{ card.text }}
    </li>
  </UContainer>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

.cards {
  --columns: 1;
  --gap: 0;

  display: grid;
  gap: var(--gap);
  margin-top: 1.25rem;
  grid-template-columns: repeat(var(--columns), minmax(10rem, 1fr));
  grid-auto-rows: 1fr;

  @media screen and (min-width: theme('screens.xs')) {
    --columns: 2;
    --gap: 1rem;
    margin-top: 5rem;

    [data-mascot] {
      display: none;
    }
  }

  @media screen and (min-width: theme('screens.sm')) {
    --columns: 3;

    &:has(> li > [data-mascot]) {
      margin-top: 9rem;
    }

    [data-mascot] {
      display: initial;
    }
  }

  > li {
    --mascot-offset: 8rem;

    align-content: center;
    font-size: 0.875rem;
    line-height: 133.333%;
    position: relative;
    color: var(--color-green-50);
    display: grid;
    grid-template-columns: 4.375rem auto;
    align-items: center;
    column-gap: 1.25rem;
    padding: 1.25rem;

    &:not(:has(> [data-mascot])) {
      grid-template-columns: auto;
      text-align: center;
    }

    @media screen and (min-width: theme('screens.xs')) {
      display: initial;
      text-align: center;
      padding: 2.5rem;
      font-size: 1.125rem;
    }

    > [data-mascot] {
      @media screen and (min-width: theme('screens.sm')) {
        max-height: 70%;
      }
    }

    &:nth-of-type(2n) {
      background-color: var(--card-background, var(--color-beige-10));
    }

    &:nth-of-type(2n + 1) {
      background: white;
    }

    &:nth-of-type(-n + 3) {
      @media screen and (min-width: theme('screens.sm')) {
        &:has(> [data-mascot]) {
          padding-top: 25%;
        }

        > [data-mascot] {
          transform: translateY(-72%);
        }
      }
    }

    &:nth-of-type(n + 4) {
      @media screen and (min-width: theme('screens.sm')) {
        &:has(> [data-mascot]) {
          padding-bottom: 25%;
        }

        > [data-mascot] {
          transform: translateY(72%);
        }
      }
    }
  }
}
</style>
